/** @format */

import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast } from "react-toastify";
import { invokeUploadFile } from "../../utils/http-service";
import { CF_APPROVAL_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";

// Reusable "Skip Step" dialog: advances a CF form exactly one step forward, capturing an
// optional reason file (uploaded internally) and a mandatory comment. On confirm it bubbles
// up { comment, skipedReasonDoc } — the parent owns the actual /move-steps call.
const CfV2SkipStepDialog = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  fromStep,
  toStep,
  stepNumberNameMap = {},
}) => {
  const [comment, setComment] = useState("");
  const [skipedReasonDoc, setSkipedReasonDoc] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Reset transient state each time the dialog is opened.
  useEffect(() => {
    if (open) {
      setComment("");
      setSkipedReasonDoc("");
      setUploading(false);
    }
  }, [open]);

  const stepLabel = (step) =>
    `Step ${step}${stepNumberNameMap[step] ? ` - ${stepNumberNameMap[step]}` : ""}`;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;
    const formData = new FormData();
    formData.append("upload", file);
    setUploading(true);
    invokeUploadFile(`${CF_APPROVAL_HOSTNAME}${REST_URLS.UPLOAD_FILES}`, formData)
      .then((res) => {
        const url = res.data[0].split("?")[0];
        setSkipedReasonDoc(url);
        toast.success("File uploaded");
      })
      .catch(() => toast.error("Failed to upload file"))
      .finally(() => setUploading(false));
  };

  const handleConfirm = () => {
    onConfirm({ comment, skipedReasonDoc: skipedReasonDoc || undefined });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <DialogTitle>Skip Step</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This will move the form from <strong>{stepLabel(fromStep)}</strong> to{" "}
          <strong>{stepLabel(toStep)}</strong>.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            uploading ? <CircularProgress size={14} /> : <CloudUploadIcon fontSize="small" />
          }
          disabled={uploading}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          {skipedReasonDoc ? "Replace Reason File" : "Upload Reason File"}
        </Button>
        {skipedReasonDoc && (
          <Typography variant="caption" sx={{ display: "block", mt: 1, color: "success.main" }}>
            File uploaded
          </Typography>
        )}
        <TextField
          label="Comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          fullWidth
          multiline
          rows={6}
          size="small"
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleConfirm}
          disabled={loading || uploading || !comment?.trim()}
        >
          {loading ? <CircularProgress size={18} /> : "Skip Step"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CfV2SkipStepDialog;
